import type { NoteView } from "@screener/protocol";
import { Button } from "@screener/uikit/components/button";
import { LoadingRegion, SkeletonLines } from "@screener/uikit/components/loading-skeleton";
import { SectionLabel } from "@screener/uikit/components/section-label";
import { Textarea } from "@screener/uikit/components/textarea";
import { useReducer, useState } from "react";
import { Ago } from "./elapsed";
import { formatClock } from "./format";
import type { NoteWriteResult } from "./notes-api";
import { initialNotesEditorState, type NotesEditorState, reduceNotesEditor } from "./notes-editor";
import type { NotesState } from "./use-notes";

interface NotesSectionProps {
  /** Монета открытой плотности — заметки берутся по ней, а не по рынку (§9). */
  base: string;
  notesState: NotesState;
  create: (base: string, text: string) => Promise<NoteWriteResult>;
  updateText: (id: string, text: string) => Promise<NoteWriteResult>;
  remove: (id: string) => Promise<"ok" | "not-found" | "unavailable">;
  /** Открыто действием «заметки» со строки — фокус сразу в поле новой записи. */
  autoFocusDraft?: boolean;
  /** Только для тестов: начальное состояние локального редактора. */
  initialEditorState?: NotesEditorState;
}

/**
 * Раздел заметок §6.4 детальной панели §9. Единственное место их правки
 * (web-density-detail «Панель показывает заметки монеты и даёт их
 * править») — действие «открыть заметки» со строки таблицы открывает эту же
 * панель на этом же разделе, второго редактора нет.
 */
export function NotesSection({
  base,
  notesState,
  create,
  updateText,
  remove,
  autoFocusDraft,
  initialEditorState,
}: NotesSectionProps) {
  const [state, dispatch] = useReducer(
    reduceNotesEditor,
    initialEditorState ?? initialNotesEditorState,
  );
  const [busy, setBusy] = useState(false);

  const handleCreate = () => {
    const text = state.draft.trim();
    if (text === "" || busy) {
      return;
    }
    setBusy(true);
    create(base, text).then((result) => {
      setBusy(false);
      dispatch({ type: result.status === "ok" ? "create-succeeded" : "create-failed" });
    });
  };

  const handleEditSave = () => {
    if (state.editing === null || busy) {
      return;
    }
    const text = state.editing.text.trim();
    if (text === "") {
      return;
    }
    setBusy(true);
    updateText(state.editing.id, text).then((result) => {
      setBusy(false);
      dispatch({ type: result.status === "ok" ? "edit-succeeded" : "edit-failed" });
    });
  };

  const handleDelete = (id: string) => {
    // Отказ записи не имеет права проглатываться молча (review stage5, п.
    // 2): «нет такой» и «не удалось сейчас» — разные исходы, и человек ведёт
    // себя по-разному в каждом.
    remove(id).then((status) => {
      dispatch(
        status === "ok"
          ? { type: "delete-succeeded" }
          : { type: "delete-failed", id, reason: status },
      );
    });
  };

  return (
    <section className="border-b border-border p-4">
      {/* Заметка живёт у монеты, а не у плотности (§9, 1.13): без имени монеты
          в заголовке раздел читается как заметка этой строки, и значок на
          всех строках монеты выглядит ошибкой. */}
      <SectionLabel as="h3">Заметки по {base}</SectionLabel>
      <p className="mt-1 text-xs text-muted-foreground">
        Общие для всех бирж и рынков монеты — значок стоит на каждой её строке.
      </p>

      {notesState.status === "loading" ? (
        <LoadingRegion label="Загружаем заметки…" className="mt-3">
          <SkeletonLines count={3} />
        </LoadingRegion>
      ) : notesState.status === "unavailable" ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Заметки недоступны — список не прочитался.
        </p>
      ) : (
        <NotesList
          notes={notesState.notes.filter((entry) => entry.base === base)}
          state={state}
          busy={busy}
          onEditStart={(note) => dispatch({ type: "edit-started", note })}
          onEditChange={(text) => dispatch({ type: "edit-changed", text })}
          onEditSave={handleEditSave}
          onEditCancel={() => dispatch({ type: "edit-cancelled" })}
          onDelete={handleDelete}
        />
      )}

      {notesState.status === "ready" ? (
        <div className="mt-3 flex flex-col gap-2">
          <Textarea
            value={state.draft}
            autoFocus={autoFocusDraft}
            placeholder="Новая заметка…"
            className="min-h-12 text-sm"
            onChange={(event) => dispatch({ type: "draft-changed", text: event.target.value })}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-6 px-2 text-xs"
              disabled={busy || state.draft.trim() === ""}
              onClick={handleCreate}
            >
              Добавить
            </Button>
            {state.error === "create" ? (
              <p className="text-xs text-destructive" role="alert">
                Заметка не сохранилась — текст остался, попробуйте ещё раз.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function NotesList({
  notes,
  state,
  busy,
  onEditStart,
  onEditChange,
  onEditSave,
  onEditCancel,
  onDelete,
}: {
  notes: NoteView[];
  state: NotesEditorState;
  busy: boolean;
  onEditStart: (note: NoteView) => void;
  onEditChange: (text: string) => void;
  onEditSave: () => void;
  onEditCancel: () => void;
  onDelete: (id: string) => void;
}) {
  if (notes.length === 0) {
    return <p className="mt-3 text-sm text-muted-foreground">Заметок пока нет.</p>;
  }

  return (
    <ul className="mt-3 flex flex-col gap-2">
      {notes.map((note) => (
        <li key={note.id} className="rounded-md border border-border p-2">
          {state.editing?.id === note.id ? (
            <div className="flex flex-col gap-2">
              <Textarea
                value={state.editing.text}
                className="min-h-12 text-sm"
                onChange={(event) => onEditChange(event.target.value)}
              />
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-6 px-2 text-xs"
                  disabled={busy || state.editing.text.trim() === ""}
                  onClick={onEditSave}
                >
                  Сохранить
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={onEditCancel}
                >
                  Отмена
                </Button>
                {state.error === "edit" ? (
                  <p className="text-xs text-destructive" role="alert">
                    Не удалось сохранить — попробуйте ещё раз.
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              <p className="text-sm whitespace-pre-wrap text-foreground">{note.text}</p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground tabular-nums">
                <span>
                  {formatClock(note.updated_at_ms)} · <Ago tsMs={note.updated_at_ms} /> назад
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onEditStart(note)}
                >
                  Править
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onDelete(note.id)}
                >
                  Удалить
                </Button>
                {state.deleteError?.id === note.id ? (
                  <p className="text-xs text-destructive" role="alert">
                    {state.deleteError.reason === "not-found"
                      ? "Этой заметки уже нет — список обновлён."
                      : "Не удалось удалить — попробуйте ещё раз."}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
